import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { z } from 'zod';
import {
  AutomationBlockerDetails,
  extensionAutomationBlockerDetailsSchema,
  formulaAutomationBlockerDetailsSchema,
  ruleAutomationBlockerDetailsSchema,
} from '@rossum/api-client/automationBlockers';
import { Typography } from '@rossum/ui/material';
import { isEmbedded } from '../../../../../constants/config';
import { boldText, routerLink } from '../../../../../lib/formaterValues';
import { annotationSideloadsSelector } from '../../../../../redux/modules/annotation/selectors';
import { schemaMapSelector } from '../../../../../redux/modules/schema/schemaMapSelector';
import { isUserAdmin } from '../../../../../redux/modules/user/selectors';
import { extensionDetailPath } from '../../../../extensions/routes';
import { MessageOrBlocker } from '../../sidebar-infosections/automation-blockers/helpers';
import { getFieldsSettingsPath, getRulesSettingsDetailPath } from '../utils';

const RuleDetailSchema = ruleAutomationBlockerDetailsSchema;

const FormulaDetailSchema = formulaAutomationBlockerDetailsSchema.refine(
  data =>
    data.hookName === 'computed_fields' ||
    data.hookId === null ||
    data.hookId === undefined,
  {
    message:
      "Must be a formula detail (hookName='computed_fields' or hookId is null/undefined)",
  }
);

const ExtensionDetailSchema = extensionAutomationBlockerDetailsSchema.refine(
  data =>
    data.hookId !== undefined &&
    data.hookName !== undefined &&
    data.hookName !== 'rules' &&
    data.hookName !== 'computed_fields',
  {
    message: 'Must be an extension detail with valid hookId and hookName',
  }
);

const isRuleDetail = (
  details: unknown
): details is z.infer<typeof RuleDetailSchema> => {
  return RuleDetailSchema.safeParse(details).success;
};

const isFormulaDetail = (
  details: unknown
): details is z.infer<typeof FormulaDetailSchema> => {
  return FormulaDetailSchema.safeParse(details).success;
};

const isExtensionDetail = (
  details: unknown
): details is z.infer<typeof ExtensionDetailSchema> => {
  return ExtensionDetailSchema.safeParse(details).success;
};

type MessageLinkTypographyProps = {
  children: React.ReactNode;
};

const MessageLinkTypography = ({ children }: MessageLinkTypographyProps) => (
  <Typography
    variant="body1"
    fontSize="small"
    color="inherit"
    sx={{
      a: {
        textDecoration: 'underline',
        fontWeight: 'bold',
        color: 'inherit',
      },
      'a:hover': {
        textDecoration: 'none',
      },
    }}
  >
    {children}
  </Typography>
);

type MessageDetailLinksProps = {
  detail: MessageOrBlocker['detail'] | AutomationBlockerDetails | null;
};

export const MessageDetailLinks = ({ detail }: MessageDetailLinksProps) => {
  const intl = useIntl();
  const history = useHistory();

  const schemaMap = useSelector(schemaMapSelector);
  const annotationSideloads = useSelector(annotationSideloadsSelector);
  const isAdmin = useSelector(isUserAdmin);

  const queueId = annotationSideloads.queue?.id;

  if (!isAdmin || isEmbedded() || !detail) {
    return null;
  }

  if (isRuleDetail(detail)) {
    return (
      <MessageLinkTypography>
        {intl.formatMessage(
          { id: 'components.documentValidation.sidebar.error.rule' },
          {
            source:
              queueId && detail.ruleId
                ? routerLink({
                    route: getRulesSettingsDetailPath(queueId, detail.ruleId),
                    backLink: history.location.pathname,
                  })
                : boldText,
            ruleName: detail.ruleName || `Rule ${detail.ruleId}`,
          }
        )}
      </MessageLinkTypography>
    );
  }

  if (isFormulaDetail(detail)) {
    const fieldName = detail.sourceSchemaId
      ? (schemaMap.get(detail.sourceSchemaId)?.label ?? detail.sourceSchemaId)
      : '';

    return (
      <MessageLinkTypography>
        {intl.formatMessage(
          { id: 'components.documentValidation.sidebar.error.formula' },
          {
            source:
              queueId && detail.sourceSchemaId
                ? routerLink({
                    route: getFieldsSettingsPath(
                      queueId,
                      detail.sourceSchemaId
                    ),
                    backLink: history.location.pathname,
                  })
                : boldText,
            fieldName,
          }
        )}
      </MessageLinkTypography>
    );
  }

  if (isExtensionDetail(detail)) {
    return (
      <MessageLinkTypography>
        {intl.formatMessage(
          { id: 'components.documentValidation.sidebar.error.extension' },
          {
            source: detail.hookId
              ? routerLink({
                  route: extensionDetailPath(detail.hookId),
                  backLink: history.location.pathname,
                })
              : boldText,
            hookName: detail.hookName || '',
          }
        )}
      </MessageLinkTypography>
    );
  }

  return null;
};
